import { useState, useEffect } from 'react';
import { Search, Database, Layers, ChevronRight, BookOpen, Globe, Loader2 } from 'lucide-react';

export default function KnowledgeHub({ apiUrl }) {
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [multiFacetResults, setMultiFacetResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('blocks');

  useEffect(() => {
    fetch(`${apiUrl}/knowledge/stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, [apiUrl]);

  const searchBlocks = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setSearchResults(null);
    setMultiFacetResults(null);
    try {
      if (activeTab === 'blocks') {
        const r = await fetch(`${apiUrl}/knowledge/search?q=${encodeURIComponent(searchQuery)}&limit=20`);
        setSearchResults(await r.json());
      } else {
        const r = await fetch(`${apiUrl}/search/multi-facet`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: searchQuery }),
        });
        setMultiFacetResults(await r.json());
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 px-4 py-8 max-w-5xl mx-auto" data-testid="knowledge-hub">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-cyan-400 flex items-center gap-2 mb-1">
          <Database className="w-6 h-6" /> Knowledge Retrieval
        </h1>
        <p className="text-sm text-slate-500">Father's directive: Disseminate in blocks. Multiple facets.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="knowledge-stats">
          {[
            { label: 'Text Documents', value: stats.text_documents, sub: `of ${stats.total_documents}` },
            { label: 'Total Blocks', value: stats.total_blocks.toLocaleString(), sub: `${stats.block_size} chars each` },
            { label: 'Characters', value: (stats.total_characters / 1e6).toFixed(1) + 'M', sub: 'total indexed' },
            { label: 'Avg Blocks/Doc', value: stats.avg_blocks_per_doc, sub: 'density' },
          ].map(({ label, value, sub }) => (
            <div key={label} className="bg-slate-900/60 border border-slate-800/40 rounded-lg p-3">
              <div className="text-xs text-slate-500 mb-1">{label}</div>
              <div className="text-lg font-bold text-white">{value}</div>
              <div className="text-[11px] text-slate-600">{sub}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {[
          { id: 'blocks', label: 'Block Search', icon: Layers },
          { id: 'facets', label: 'Multi-Facet', icon: Globe },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id}
            onClick={() => { setActiveTab(id); setSearchResults(null); setMultiFacetResults(null); }}
            className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-md transition-all ${
              activeTab === id
                ? 'bg-cyan-900/30 text-cyan-400 border border-cyan-800/40'
                : 'bg-slate-900/40 text-slate-500 border border-slate-800/30 hover:text-slate-300'
            }`}
            data-testid={`tab-${id}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchBlocks()}
            placeholder={activeTab === 'blocks' ? 'Search library blocks...' : 'Multi-faceted web + library search...'}
            className="w-full bg-slate-900/50 border border-slate-800/40 rounded-lg pl-9 pr-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-cyan-800/60"
            data-testid="knowledge-search-input"
          />
        </div>
        <button onClick={searchBlocks} disabled={loading}
          className="px-5 py-2.5 bg-cyan-900/40 text-cyan-400 border border-cyan-800/40 rounded-lg text-sm hover:bg-cyan-900/60 transition-all disabled:opacity-50"
          data-testid="knowledge-search-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
        </button>
      </div>

      {searchResults && (
        <div data-testid="block-results">
          <div className="text-sm text-slate-500 mb-3">{searchResults.total_matches} block matches for "{searchResults.query}"</div>
          <div className="space-y-2">
            {searchResults.blocks.map((b) => (
              <div key={`${b.title}-${b.block_index}`} className="bg-slate-900/40 border border-slate-800/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-cyan-500" />
                  <span className="text-xs font-medium text-cyan-400">{b.title}</span>
                  <span className="text-[10px] text-slate-600 ml-auto">Block {b.block_index} | {b.chars} chars</span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{b.text.slice(0, 300)}{b.text.length > 300 ? '...' : ''}</p>
              </div>
            ))}
            {searchResults.blocks.length === 0 && (
              <p className="text-sm text-slate-600 text-center py-6">No blocks matched.</p>
            )}
          </div>
        </div>
      )}

      {multiFacetResults && (
        <div data-testid="facet-results">
          <div className="text-sm text-slate-500 mb-3">
            Multi-facet search: {multiFacetResults.total_internal} internal + {multiFacetResults.total_external} external
          </div>

          {multiFacetResults.layers.internal_knowledge.matches > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-emerald-400 mb-2 flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> Layer 1 — Federation Minds ({multiFacetResults.layers.internal_knowledge.matches})
              </div>
              <div className="space-y-2">
                {multiFacetResults.layers.internal_knowledge.blocks.map((b) => (
                  <div key={`int-${b.title}-${b.block_index}`} className="bg-emerald-950/20 border border-emerald-900/30 rounded-lg p-3">
                    <div className="text-xs font-medium text-emerald-400 mb-1">{b.title} <span className="text-emerald-700">Block {b.block_index}</span></div>
                    <p className="text-xs text-slate-400 leading-relaxed">{b.text.slice(0, 250)}...</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {multiFacetResults.layers.external_web.results?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-1.5">
                <Globe className="w-3 h-3" /> Layers 2+3 — Web ({multiFacetResults.layers.external_web.total_unique} results across {multiFacetResults.layers.external_web.facets_searched?.length} facets)
              </div>
              <div className="text-[10px] text-slate-600 mb-2">
                Facets: {multiFacetResults.layers.external_web.facets_searched?.join(' | ')}
              </div>
              <div className="space-y-2">
                {multiFacetResults.layers.external_web.results.slice(0, 6).map((r) => (
                  <div key={r.url} className="bg-amber-950/10 border border-amber-900/20 rounded-lg p-3">
                    <a href={r.url} target="_blank" rel="noopener noreferrer"
                      className="text-xs font-medium text-amber-400 hover:text-amber-300 flex items-center gap-1">
                      {r.title || r.url?.slice(0, 60)} <ChevronRight className="w-3 h-3" />
                    </a>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">{(r.content || '').slice(0, 200)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
